import classNames from "classnames";
import { memo, MouseEvent, useState } from "react";
import { Tooltip } from "./arbiter-components/tooltip/tooltip";
import { Flex } from "./flex";
import { Link } from "./link";
import * as styles from "./product-list-row.module.css";
import { Text } from "./text";

const stopPropagation = (event: MouseEvent) => {
  event.stopPropagation();
};

export const ProductListItem = memo(function ProductListItem({
  title,
  imageUrl,
  variantTitle,
  sku,
  serialNumbers,
  productHref,
  disableTooltip = false,
  imageTooltipPlacement = "left",
}: {
  title: string;
  imageUrl: string | null | undefined;
  variantTitle: string | null | undefined;
  sku: string | null | undefined;
  serialNumbers?: readonly string[] | null;
  productHref?: string | null;
  disableTooltip?: boolean;
  imageTooltipPlacement?: "left" | "right" | "top" | "bottom";
}) {
  const [productTitleElementRef, setProductTitleElementRef] =
    useState<HTMLParagraphElement | null>(null);

  const titleIsOverflowing = productTitleElementRef
    ? productTitleElementRef.scrollWidth > productTitleElementRef.clientWidth
    : false;

  return (
    <Tooltip title={!titleIsOverflowing || disableTooltip ? "" : title}>
      <Flex gap="md" grow={1} minW="none">
        {imageUrl ? (
          <Tooltip
            arrow
            enterDelay={700}
            enterNextDelay={500}
            placement={imageTooltipPlacement}
            title={
              <img
                alt={title}
                className={classNames(styles.productImage, styles.tooltip)}
                src={imageUrl}
              />
            }
          >
            <img
              alt={title}
              className={styles.productImage}
              data-web="ProductListItem"
              height={64}
              src={imageUrl}
              width={64}
            />
          </Tooltip>
        ) : null}
        <Flex flex={1} justify="space-between" minW="none">
          <Flex flexDirection="column" gap="xs" justify="center" minW="none">
            <Text
              className={styles.wrapAndEllipsesOverflow}
              fontSize="xs"
              fontWeight="medium"
              ref={setProductTitleElementRef}
            >
              {productHref ? (
                <Link
                  onClick={stopPropagation}
                  rel="noreferrer"
                  target="_blank"
                  to={productHref}
                >
                  {title}
                </Link>
              ) : (
                title
              )}
            </Text>
            {variantTitle && variantTitle !== "Default Title" && (
              <Text
                className={styles.wrapAndEllipsesOverflow}
                fontSize="xxs"
                textColor="secondary"
              >
                {variantTitle.replace(" / ", ", ")}
              </Text>
            )}
            {sku && (
              <Text
                className={styles.wrapAndEllipsesOverflow}
                fontSize="xxs"
                textColor="secondary"
              >
                {productHref ? (
                  <Link
                    onClick={stopPropagation}
                    rel="noreferrer"
                    target="_blank"
                    to={productHref}
                  >
                    {sku}
                  </Link>
                ) : (
                  sku
                )}
              </Text>
            )}
            {serialNumbers?.map((serialNumber) => (
              <Text
                className={styles.wrapAndEllipsesOverflow}
                fontSize="xxs"
                key={serialNumber}
                textColor="secondary"
              >
                {serialNumber}
              </Text>
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Tooltip>
  );
});
